import CommonTab from "@/components/Common/Tab";
import ContentLayout from "@/layouts/ContentLayout";
import { TabMenu } from "@/types/Shared/tab";
import { useEffect, useMemo, useState } from "react";
import DataTable from "@/components/Common/DataTable";
import { TaskStatus } from "@/consts/Task";
import CommonImg from "@/components/Common/Img";
import { Checkbox } from "@/components/ui/checkbox";
import CommonInput from "@/components/Common/Input";
import { Button } from "@/components/ui/button";
import SearchIcon from "@/assets/images/search-icon.svg";
import { ReadProjectGroupsTasksAllRequest } from "@/types/project";
import { useReadProjectGroupsTasksAll } from "@/libs/react-query/hooks/useProjectQuery";
import { useNavigate } from "react-router-dom";
import { createTaskColumns } from "./columns";

export default function TaskManagement() {
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [targets, setTargets] = useState<TaskStatus[]>([
    TaskStatus.OPENED,
    TaskStatus.IN_PROGRESS,
    TaskStatus.REVIEW,
    TaskStatus.CLOSED,
  ]);

  const navigate = useNavigate();

  const tabData: TabMenu[] = [
    {
      label: "프로젝트 관리",
      path: "/management/projects",
    },
    { label: "작업 관리", path: "/management/tasks" },
  ];

  const [filters, setFilters] = useState<ReadProjectGroupsTasksAllRequest>({
    targets,
    filters: {
      is_mine: showOnlyMine,
      keyword: "",
    },
  });

  const { data: { tasks = [] } = {}, refetch } =
    useReadProjectGroupsTasksAll(filters);

  const dataWithClickHandlers = useMemo(() => {
    return tasks.map((item) => ({
      ...item,
      onRowClick: () =>
        navigate(
          `/management/projects/${item.project_group_id}/tasks/${item.task_id}`,
          {
            state: {
              group_id: item.project_group_id,
            },
          }
        ),
    }));
  }, [tasks]);

  const onRowClick = (projectGroupId: string) => {
    navigate(`/management/projects/${projectGroupId}`);
  };

  const columns = useMemo(() => createTaskColumns(onRowClick), []);

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        keyword: searchKeyword,
      },
    }));
  };

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      targets,
      filters: {
        ...prev.filters,
        is_mine: showOnlyMine,
      },
    }));
  }, [showOnlyMine, targets]);

  useEffect(() => {
    refetch();
  }, [filters]);
  return (
    <ContentLayout>
      <CommonTab variants="string" data={tabData} />

      <div className="p-5 bg-white border border-gray-D rounded-xl">
        <DataTable
          columns={columns}
          data={dataWithClickHandlers}
          filterComponent={
            <FilterComponent
              targets={targets}
              setTargets={setTargets}
              searchKeyword={searchKeyword}
              setSearchKeyword={setSearchKeyword}
              showOnlyMine={showOnlyMine}
              setShowOnlyMine={setShowOnlyMine}
              showCanceled={showCanceled}
              setShowCanceled={setShowCanceled}
              onSearch={handleSearch}
            />
          }
        />
      </div>
    </ContentLayout>
  );
}

const FilterComponent = ({
  targets,
  setTargets,
  searchKeyword,
  setSearchKeyword,
  showOnlyMine,
  setShowOnlyMine,
  showCanceled,
  setShowCanceled,
  onSearch,
}: {
  targets: TaskStatus[];
  setTargets: (val: TaskStatus[]) => void;
  searchKeyword: string;
  setSearchKeyword: (val: string) => void;
  showOnlyMine: boolean;
  setShowOnlyMine: (val: boolean) => void;
  showCanceled: boolean;
  setShowCanceled: (val: boolean) => void;
  onSearch: () => void;
}) => {
  const handleOnlyMineChange = (checked: boolean) => {
    setShowOnlyMine(checked);
  };

  const handleCanceledChange = (checked: boolean) => {
    setShowCanceled(checked);
    if (checked) {
      setTargets([...targets, TaskStatus.CANCELED]);
    } else {
      setTargets(targets.filter((target) => target !== TaskStatus.CANCELED));
    }
  };

  return (
    <div className="p-2 mb-3 flex flex-row justify-between items-center">
      <div className="flex items-center space-x-2 py-2 pl-1 pr-3">
        <Checkbox
          id="filter-checkbox"
          className="border-gray-D bg-white"
          checked={showOnlyMine}
          onCheckedChange={handleOnlyMineChange}
        />
        <label
          htmlFor="filter-checkbox"
          className="text-gray-B text-xs font-semibold"
        >
          나와 관련된 작업 목록만 보기
        </label>

        <Checkbox
          id="canceled-checkbox"
          className="border-gray-D bg-white"
          checked={showCanceled}
          onCheckedChange={handleCanceledChange}
        />
        <label
          htmlFor="canceled-checkbox"
          className="text-gray-B text-xs font-semibold"
        >
          취소된 작업 내역까지 보기
        </label>
      </div>

      <div className="flex items-center space-x-2">
        <CommonInput
          placeholder="검색해서 찾아보세요!"
          defaultValue={searchKeyword}
          className="border-gray-D bg-white py-2 px-3 text-xs font-semibold"
          onChange={(value) => setSearchKeyword(value)}
          onKeyDownEnter={onSearch}
        />
        <Button
          className="p-0 w-[30px] h-[30px] bg-dark-brown rounded-sm"
          onClick={onSearch}
        >
          <CommonImg
            className="p-1 bg-dark-brown rounded-sm size-[26px] cursor-pointer"
            src={SearchIcon}
            alt="Search"
          />
        </Button>
      </div>
    </div>
  );
};
